import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { buildInboxUpdatePayload } from './messengerTemplateHelper';

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'conversation.can_reply_changed': this.onConversationCanReplyChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'evolution_api.connect': this.onEvolutionApiConnect,
      'evolution_api.linked_change': this.onEvolutionApiLinkedChange,
      'first.reply.created': this.onFirstReplyCreated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'conversation.participants_updated': this
        .onConversationParticipantsUpdated,
      'user.updated': this.onAgentUpdate,
      'agent.removed': this.onAgentRemoved,
      'group_participants.updated': this.onGroupParticipantsUpdated,
      'inbox.deleted': this.onInboxDeleted,
      'inbox.templates_updated': this.onInboxTemplatesUpdated,
      'inbox.reauthorization_changed': this.onInboxReauthorizationChanged,
      'webhook.status_changed': this.onWebhookStatusChanged,
    };
  }

  /**
   * Maneja el evento de eliminación de bandeja eliminando el inbox del store.
   * @param {Object} data - Datos del evento con el ID de la bandeja eliminada.
   */
  onInboxDeleted = data => {
    const inboxId = data.inbox_id;
    this.app.$store.commit('inboxes/DELETE_INBOXES', inboxId);
  };

  /**
   * Actualiza en el store los campos del inbox recibidos (p. ej. message_templates tras el sync),
   * para que el botón de plantillas aparezca/actualice sin recargar la página.
   *
   * Contrato del evento `inbox.templates_updated` (emitido por
   * ActionCableListener#inbox_templates_updated): llega `{ inbox_id, message_templates, account_id }`.
   * `account_id` lo agrega el backend a todos los eventos (broadcast) y lo exige `isAValidEvent`
   * para descartar eventos de otra cuenta. Aquí solo se mergean `inbox_id` + `message_templates`
   * (merge parcial por id, no se asume el inbox completo). Si en el futuro se necesitan más campos,
   * ampliarlos explícitamente tanto en el payload del listener (backend) como en este handler.
   * @param {Object} data - { inbox_id, message_templates, account_id }
   */
  onInboxTemplatesUpdated = data => {
    // buildInboxUpdatePayload protege contra clobber: omite message_templates si viene
    // null/undefined (no sobrescribe el estado bueno). Un arreglo vacío [] sí se aplica.
    // Devuelve null si falta inbox_id (no hay inbox que actualizar) → no se despacha.
    //
    // A diferencia de onInboxDeleted (que hace commit directo de una mutación trivial), aquí se
    // hace dispatch de la acción `updateInboxAttributes`: centraliza el merge en una acción
    // reutilizable y testeada, acorde al payload ya saneado por buildInboxUpdatePayload.
    const payload = buildInboxUpdatePayload(data);
    if (!payload) return;
    this.app.$store.dispatch('inboxes/updateInboxAttributes', payload);
  };

  // Actualiza en vivo `reauthorization_required` del inbox en el store: banner, botón de
  // plantillas, warning del sidebar y badge de Settings derivan de este campo. Payload
  // { inbox_id, reauthorization_required, account_id }; `false` es válido (reautorizado).
  onInboxReauthorizationChanged = data => {
    if (data?.inbox_id == null) return;
    if (typeof data.reauthorization_required !== 'boolean') return;
    this.app.$store.dispatch('inboxes/updateInboxAttributes', {
      id: data.inbox_id,
      reauthorization_required: data.reauthorization_required,
    });
  };

  // Maneja la actualización de participantes del grupo.
  // Despacha la acción para actualizar los datos en el store.
  onGroupParticipantsUpdated = data => {
    this.app.$store.dispatch('updateGroupParticipants', data);
  };

  isAValidEvent = data => {
    return this.app.$store.getters.getCurrentAccountId === data.account_id;
  };

  onMessageUpdated = data => {
    this.app.$store.dispatch('updateMessage', data);
  };

  onConversationCanReplyChange = data => {
    this.app.$store.dispatch('updateConversation', data);
  };

  onPresenceUpdate = data => {
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
    this.fetchConversationStats();
  };

  onConversationCreated = data => {
    this.app.$store.dispatch('addConversation', data);
    this.fetchConversationStats();
  };

  onConversationRead = data => {
    this.app.$store.dispatch('updateConversation', data);
  };

  // Actualiza los participantes de una conversación cuando detecta el evento de actualización de participantes que viene del backend.
  onConversationParticipantsUpdated = data => {
    const { conversation, participants } = data;

    this.app.$store.dispatch('conversationWatchers/updateByBroadcast', {
      conversationId: conversation.id,
      participantsUpdated: participants,
    });
  };

  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    DashboardAudioNotificationHelper.onNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
  };

  onReload = () => window.location.reload();

  onStatusChange = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onConversationUpdated = data => {
    const currentInbox = this.app.$store.getters.getSelectedInbox;
    const shouldFetchStats = !currentInbox || data.inbox_id === currentInbox;

    this.app.$store.dispatch('updateConversation', data);

    if (shouldFetchStats) {
      this.fetchConversationStats();
    }
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 30000);
  };

  fetchConversationStats = () => {
    bus.$emit('fetch_conversation_stats');
    bus.$emit('fetch_overview_reports');
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  /**
   * Maneja la actualización del agente y desencadena una acción de verificación de validez.
   */
  onAgentUpdate = () => {
    this.app.$store.dispatch('validityCheck');
  };

  // Maneja la eliminación del agente cuando se recibe el evento de eliminación desde el backend.
  onAgentRemoved = data => {
    this.app.$store.dispatch('agents/deleteByBroadcast', data.id);
  };

  onEvolutionApiConnect = data => {
    this.app.$store.dispatch('inboxes/evolutionApiConnect', data);
  };

  // Maneja la conexion y desconexión de la API de Evolution y despacha la acción correspondiente.
  onEvolutionApiLinkedChange = data => {
    this.app.$store.dispatch('inboxes/evolutionApiLinkedChange', data);
  };

  onNotificationCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);
  };

  onFirstReplyCreated = () => {
    bus.$emit('fetch_overview_reports');
  };

  // Maneja el cambio de estado del webhook y despacha la acción para actualizar el estado en el store.
  onWebhookStatusChanged = data => {
    this.app.$store.dispatch('webhooks/updateWebhookStatus', data);
  };
}

export default {
  init(pubsubToken) {
    return new ActionCableConnector(window.WOOT, pubsubToken);
  },
};
